import React, { useState, useEffect, useRef } from 'react';
import { getVehicles, getPositions, getAuth } from '../utils/api';
import CmsV6VideoPlayer from '../components/CmsV6VideoPlayer';
import VehicleMap from '../components/VehicleMap';
import useReverseGeocode from '../utils/useReverseGeocode';

const MAX_VEHICLES  = 4;
const GRID_COLS     = 5;   // map + max 4 channels — always 5 equal columns
const CELL_H        = 180; // row height for multi-vehicle rows (px)

// ── VehicleRow ───────────────────────────────────────────────────────────────
// Extracted as a real component so it can call useReverseGeocode per-vehicle.
function VehicleRow({ v, pos, chCount, stopped, onToggleStop, renderCell }) {
  const address  = useReverseGeocode(pos);
  const channels = Array.from({ length: chCount }, (_, i) => i);
  const isOnline = pos?.online === true;
  const vStopped = channels.every(ch => stopped.has(`${v.devId}-${ch}`));

  return (
    <div style={{ flexShrink: 0, borderRadius: 10, overflow: 'hidden', background: '#111' }}>
      {/* Header strip */}
      <div style={{ height: 36, background: '#1c1c1c', display: 'flex', alignItems: 'center', padding: '0 10px', gap: 8, borderBottom: '1px solid #222' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: isOnline ? '#4CAF50' : '#555', flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: '#ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>
          {v.displayName || v.devId}
        </span>
        {v.companyName && (
          <span style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap', flexShrink: 0 }}>
            · {v.companyName}
          </span>
        )}
        {pos?.speed > 0 && (
          <span style={{ fontSize: 11, color: '#bbb', whiteSpace: 'nowrap', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
            {pos.speed} km/h
          </span>
        )}
        {/* Address: fills remaining space, 2-line clamp, pulses while resolving */}
        <span style={{
          fontSize: 11, color: '#aaa', flex: 1, minWidth: 0,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden', lineHeight: '1.25',
          animation: address === null && pos?.lat ? 'addr-pulse 1.4s ease-in-out infinite' : 'none',
        }}>
          {address ?? (pos?.lat ? '...' : '')}
        </span>
        <div style={{ flexShrink: 0 }}>
          <button onClick={() => onToggleStop(v)}
            style={{ padding: '2px 8px', fontSize: 10, border: '0.5px solid #333', borderRadius: 4, background: vStopped ? '#185FA5' : 'transparent', color: vStopped ? 'white' : '#888', cursor: 'pointer' }}>
            {vStopped ? '▶ Play' : '■ Stop'}
          </button>
        </div>
      </div>
      {/* Video row: always 5 equal columns (map + up to 4 channels) */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`, gap: 3, height: CELL_H }}>
        <div style={{ position: 'relative', overflow: 'hidden' }}>
          <VehicleMap pos={pos} size="mini" />
        </div>
        {channels.map(ch => renderCell(v, ch))}
      </div>
    </div>
  );
}

// ── SingleVehicleHeader ──────────────────────────────────────────────────────
// Extracted so it can call useReverseGeocode (hooks can't run in render helpers).
function SingleVehicleHeader({ v, pos, isAllStopped, onToggleAllStop, onToggleFullscreen }) {
  const address  = useReverseGeocode(pos);
  const isOnline = pos?.online === true;

  return (
    <div style={{ background: 'white', border: '0.5px solid #e8e8e8', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, minHeight: 40 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: isOnline ? '#4CAF50' : '#ccc', flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 600, color: '#222', whiteSpace: 'nowrap' }}>
        {v.displayName || v.devId}
      </span>
      {v.companyName && (
        <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', flexShrink: 0 }}>
          · {v.companyName}
        </span>
      )}
      {pos?.speed > 0 && (
        <span style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          {pos.speed} km/h
        </span>
      )}
      {pos?.lat && (
        <span style={{
          fontSize: 11, color: '#999', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          animation: address === null ? 'addr-pulse 1.4s ease-in-out infinite' : 'none',
        }}>
          {address ?? '...'}
        </span>
      )}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexShrink: 0 }}>
        <button onClick={onToggleAllStop}
          style={{ padding: '4px 9px', fontSize: 11, border: '0.5px solid #ccc', borderRadius: 6, background: isAllStopped ? '#185FA5' : 'white', color: isAllStopped ? 'white' : '#666', cursor: 'pointer' }}>
          {isAllStopped ? 'Resume All' : 'Stop All'}
        </button>
        <button onClick={onToggleFullscreen}
          style={{ padding: '4px 9px', fontSize: 12, border: '0.5px solid #ccc', borderRadius: 6, background: 'white', color: '#666', cursor: 'pointer' }}
          title="Fullscreen">⛶</button>
      </div>
    </div>
  );
}

// ── LiveViewPage ─────────────────────────────────────────────────────────────
export default function LiveViewPage() {
  const [vehicles,  setVehicles]  = useState([]);
  const [positions, setPositions] = useState([]);
  const [selected,  setSelected]  = useState([]);       // vehicle objects, max 4
  const [stopped,   setStopped]   = useState(new Set()); // "devId-ch" keys
  const contentRef  = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [v, p] = await Promise.all([getVehicles(), getPositions()]);
        setVehicles(v.vehicles  || []);
        setPositions(p.positions || []);
      } catch {}
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  // Drop stopped-cell keys for vehicles that were deselected
  useEffect(() => {
    if (!stopped.size) return;
    const keep = new Set([...stopped].filter(k => selected.some(v => k.startsWith(v.devId + '-'))));
    if (keep.size !== stopped.size) setStopped(keep);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPos     = (devId) => positions.find(p => p.vehiIdno === devId) || null;
  const getChCount = (v)     => Math.max(1, Math.min(4, v.channelCount || 1));
  const jsession   = getAuth()?.jsession;

  const toggle = (v) => {
    setSelected(prev => {
      const idx = prev.findIndex(s => s.devId === v.devId);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      if (prev.length >= MAX_VEHICLES) return prev;
      return [...prev, v];
    });
  };

  const isAllStopped = selected.length > 0 && selected.every(v =>
    Array.from({ length: getChCount(v) }, (_, i) => i).every(ch => stopped.has(`${v.devId}-${ch}`))
  );

  const toggleAllStop = () => {
    if (isAllStopped) {
      setStopped(new Set());
    } else {
      const all = new Set();
      selected.forEach(v =>
        Array.from({ length: getChCount(v) }, (_, i) => i).forEach(ch => all.add(`${v.devId}-${ch}`))
      );
      setStopped(all);
    }
  };

  const toggleCell = (devId, ch) => {
    const key = `${devId}-${ch}`;
    setStopped(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  };

  const toggleVehicleStop = (v) => {
    const chs     = Array.from({ length: getChCount(v) }, (_, i) => i);
    const allStop = chs.every(ch => stopped.has(`${v.devId}-${ch}`));
    setStopped(prev => {
      const s = new Set(prev);
      if (allStop) { chs.forEach(ch => s.delete(`${v.devId}-${ch}`)); }
      else         { chs.forEach(ch => s.add(`${v.devId}-${ch}`)); }
      return s;
    });
  };

  const toggleFullscreen = () => {
    document.fullscreenElement
      ? document.exitFullscreen?.()
      : contentRef.current?.requestFullscreen?.();
  };

  const renderCell = (v, ch) => {
    const key       = `${v.devId}-${ch}`;
    const isStopped = stopped.has(key);
    return (
      <div key={key} className="video-cell"
        style={{ position: 'relative', overflow: 'hidden', background: '#0a0a0a' }}>
        {isStopped ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <div style={{ color: '#444', fontSize: 11 }}>CH{ch + 1} paused</div>
            <button onClick={() => toggleCell(v.devId, ch)}
              style={{ padding: '3px 10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>
              Resume
            </button>
          </div>
        ) : (
          <CmsV6VideoPlayer vehicleId={v.devId} channel={ch} jsession={jsession} />
        )}
        <button className="cell-x" onClick={() => toggleCell(v.devId, ch)}
          title={isStopped ? 'Resume' : 'Stop'}
          style={{
            position: 'absolute', top: 4, right: 4, width: 18, height: 18, padding: 0,
            background: 'rgba(0,0,0,.75)', border: 'none', borderRadius: '50%',
            color: '#ccc', fontSize: 13, lineHeight: 1, cursor: 'pointer',
            opacity: 0, transition: 'opacity .15s',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
      </div>
    );
  };

  // Single-vehicle: 16:9 for 1-2 ch, 2×2 for 3-4 ch, map below
  const renderSingle = (v) => {
    const chCount  = getChCount(v);
    const channels = Array.from({ length: chCount }, (_, i) => i);
    const cols     = chCount === 1 ? 1 : 2;
    const rows     = Math.ceil(chCount / cols);
    const pos      = getPos(v.devId);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflow: 'hidden' }}>
        <div style={{
          ...(chCount <= 2 ? { aspectRatio: '16/9' } : { flex: 1 }),
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap: 3, borderRadius: 10, overflow: 'hidden',
        }}>
          {channels.map(ch => renderCell(v, ch))}
        </div>
        <div style={{ height: 200, flexShrink: 0, borderRadius: 10, overflow: 'hidden' }}>
          <VehicleMap pos={pos} size="mini" />
        </div>
      </div>
    );
  };

  // Multi-vehicle: VehicleRow component renders header + one-row grid per vehicle
  const renderMulti = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, overflowY: 'auto' }}>
      {selected.map(v => (
        <VehicleRow
          key={v.devId}
          v={v}
          pos={getPos(v.devId)}
          chCount={getChCount(v)}
          stopped={stopped}
          onToggleStop={toggleVehicleStop}
          renderCell={renderCell}
        />
      ))}
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12, height: 'calc(100vh - 110px)' }}>

      {/* Vehicle list */}
      <div style={{ background: 'white', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 14px', borderBottom: '0.5px solid #eee', fontSize: 12, fontWeight: 500, color: '#666' }}>
          Vehicles ({vehicles.length})
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {vehicles.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: '#aaa', textAlign: 'center' }}>No vehicles found</div>
          )}
          {vehicles.map((v, i) => {
            const pos      = getPos(v.devId);
            const isOnline = pos?.online === true;
            const isSel    = selected.some(s => s.devId === v.devId);
            const selIdx   = selected.findIndex(s => s.devId === v.devId);
            const atMax    = !isSel && selected.length >= MAX_VEHICLES;
            return (
              <div key={i} onClick={() => !atMax && toggle(v)}
                style={{ padding: '10px 14px', cursor: atMax ? 'default' : 'pointer', borderBottom: '0.5px solid #f5f5f5', background: isSel ? '#E6F1FB' : 'transparent', opacity: atMax ? 0.45 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" readOnly checked={isSel}
                    style={{ pointerEvents: 'none', accentColor: '#185FA5', flexShrink: 0 }} />
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: isOnline ? '#4CAF50' : '#ccc', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.displayName || `Vehicle ${i + 1}`}
                    </div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>
                      {isOnline ? 'Online' : 'Offline'}{pos?.speed > 0 ? ` · ${pos.speed} km/h` : ''}
                    </div>
                  </div>
                  {isSel && (
                    <span style={{ fontSize: 9, background: '#185FA5', color: 'white', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {selIdx + 1}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {selected.length > 0 && (
          <div style={{ padding: '6px 14px', borderTop: '0.5px solid #eee', fontSize: 10, color: '#999' }}>
            {selected.length} / {MAX_VEHICLES} selected
          </div>
        )}
      </div>

      {/* Main view */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
        {selected.length === 0 ? (
          <div style={{ flex: 1, background: '#111', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
            <svg width="44" height="44" fill="#444" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
            </svg>
            <div style={{ color: '#666', fontSize: 13 }}>Select up to {MAX_VEHICLES} vehicles to view live feed</div>
          </div>
        ) : (
          <>
            {selected.length === 1 ? (
              <SingleVehicleHeader
                v={selected[0]}
                pos={getPos(selected[0].devId)}
                isAllStopped={isAllStopped}
                onToggleAllStop={toggleAllStop}
                onToggleFullscreen={toggleFullscreen}
              />
            ) : (
              <div style={{ background: 'white', border: '0.5px solid #e8e8e8', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 500 }}>{selected.length} vehicles</span>
                <span style={{ fontSize: 10, color: '#ddd' }}>|</span>
                <span style={{ fontSize: 10, color: '#aaa' }}>CMSV6 live</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button onClick={toggleAllStop}
                    style={{ padding: '4px 9px', fontSize: 11, border: '0.5px solid #ccc', borderRadius: 6, background: isAllStopped ? '#185FA5' : 'white', color: isAllStopped ? 'white' : '#666', cursor: 'pointer' }}>
                    {isAllStopped ? 'Resume All' : 'Stop All'}
                  </button>
                  <button onClick={toggleFullscreen}
                    style={{ padding: '4px 9px', fontSize: 12, border: '0.5px solid #ccc', borderRadius: 6, background: 'white', color: '#666', cursor: 'pointer' }}
                    title="Fullscreen">⛶</button>
                </div>
              </div>
            )}

            <div ref={contentRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 12 }}>
              {selected.length === 1 ? renderSingle(selected[0]) : renderMulti()}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes addr-pulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 0.9; } }
        .video-cell:hover .cell-x { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
